<template>
  <div class="background-removal">
    <div class="compressor-wrapper" v-if="currentLanguage == 'en'">
      <div
        class="drop-zone"
        :class="{ dragging: isDragging, 'has-file': originalImage }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="!originalImage && $refs.fileInput.click()"
      >
        <template v-if="!originalImage">
          <div class="drop-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
              <path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"/>
            </svg>
          </div>
          <p class="drop-text">Drag & Drop your image here</p>
          <p class="drop-hint">or</p>
          <button class="choose-btn" @click.stop="$refs.fileInput.click()">Choose File</button>
          <p class="drop-formats">Supports JPG, PNG, WebP · Up to 20MB</p>
        </template>

        <template v-else>
          <div class="preview-area">
            <div class="preview-card">
              <div class="preview-label">Original</div>
              <img :src="originalImage" alt="Original" class="preview-img" />
              <div class="preview-meta">
                <span class="dim-badge">{{ originalDims }}</span>
              </div>
            </div>

<div class="preview-arrow">
              <svg v-if="!isProcessing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
              <template v-else>
                <div class="spinner"></div>
                <div class="processing-status" role="status" aria-live="polite"><strong>{{ processingMessage }}</strong><span>{{ elapsedLabel }}</span><div class="progress-track"><span :style="{ width: visualProgress + '%' }"></span></div><small>Keep this page open. Processing may take 1–3 minutes, and the result can need extra time after the model finishes loading.</small></div>
              </template>
            </div>

            <div class="preview-card">
              <div class="preview-label">Result</div>
              <img 
                v-if="processedImage" 
                :src="processedImage" 
                alt="Result" 
                class="preview-img" 
                :class="{'transparent-bg': backgroundColor === 'transparent'}"
              />
              <div v-else class="preview-placeholder">
                <span v-if="isProcessing">Removing background...</span>
                <span v-else>Waiting for processing</span>
              </div>
            </div>
          </div>

          <button class="reset-btn" @click.stop="reset">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-3.36"/>
            </svg>
            Upload New Image
          </button>
        </template>
      </div>

      <div class="settings-grid" >
        <div class="settings-card">
          <div class="card-title">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/></svg>
            Background Color
          </div>
          
          <label class="field-label">Choose Color</label>
          <div class="color-picker-grid">
            <div class="color-circle transparent-bg" :class="{active: backgroundColor === 'transparent'}" @click="changeBG('transparent')" title="Transparent"></div>
            <div class="color-circle" style="background: #ffffff;" :class="{active: backgroundColor === '#ffffff'}" @click="changeBG('#ffffff')" title="White"></div>
            <div class="color-circle" style="background: #f3f4f6;" :class="{active: backgroundColor === '#f3f4f6'}" @click="changeBG('#f3f4f6')" title="Gray"></div>
            <div class="color-circle" style="background: #6366f1;" :class="{active: backgroundColor === '#6366f1'}" @click="changeBG('#6366f1')" title="Purple"></div>
            <div class="custom-color-input" title="Custom Color">
              <input type="color" v-model="customColor" @input="changeBG(customColor)">
            </div>
          </div>
        </div>

        <div class="settings-card">
          <div class="card-title">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            Export Options
          </div>

          <label class="field-label">Output Format</label>
          <div class="select-wrap">
            <select v-model="outputFormat" @change="applySettings">
              <option value="image/png">PNG (Best for Transparency)</option>
              <option value="image/jpeg">JPG (Smallest size, no transparency)</option>
              <option value="image/webp">WebP (Best for Web)</option>
            </select>
            <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </div>

      <div class="action-area" v-if="processedImage">
        <button class="download-btn" @click="downloadImage">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Download Final Image
        </button>
      </div>
    </div>


    <div class="compressor-wrapper" v-else>
      <div
        class="drop-zone"
        :class="{ dragging: isDragging, 'has-file': originalImage }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="!originalImage && $refs.fileInput.click()"
      >
        <template v-if="!originalImage">
          <div class="drop-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
              <path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"/>
            </svg>
          </div>
          <p class="drop-text">اسحب وأفلت الصورة هنا</p>
          <p class="drop-hint">أو</p>
          <button class="choose-btn" @click.stop="$refs.fileInput.click()">اختر صورة</button>
          <p class="drop-formats">يدعم JPG و PNG و WebP · حتى 20MB</p>
        </template>

        <template v-else>
          <div class="preview-area">
            <div class="preview-card">
              <div class="preview-label">الأصلية</div>
              <img :src="originalImage" alt="Original" class="preview-img" />
              <div class="preview-meta">
                <span class="dim-badge">{{ originalDims }}</span>
              </div>
            </div>

<div class="preview-arrow">
              <svg style="transform: scaleX(-1);" v-if="!isProcessing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
              <template v-else>
                <div class="spinner"></div>
                <div class="processing-status" role="status" aria-live="polite"><strong>{{ processingMessage }}</strong><span>{{ elapsedLabel }}</span><div class="progress-track"><span :style="{ width: visualProgress + '%' }"></span></div><small>اترك الصفحة مفتوحة. قد تستغرق المعالجة من دقيقة إلى 3 دقائق، وقد تحتاج النتيجة وقتًا إضافيًا بعد انتهاء تحميل النموذج.</small></div>
              </template>
            </div>

            <div class="preview-card">
              <div class="preview-label">النتيجة</div>
              <img 
                v-if="processedImage" 
                :src="processedImage" 
                alt="Result" 
                class="preview-img" 
                :class="{'transparent-bg': backgroundColor === 'transparent'}"
              />
              <div v-else class="preview-placeholder">
                <span v-if="isProcessing">جاري إزالة الخلفية...</span>
                <span v-else>في انتظار المعالجة</span>
              </div>
            </div>
          </div>

          <button class="reset-btn" @click.stop="reset">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-3.36"/>
            </svg>
            ارفع صورة جديدة
          </button>
        </template>
      </div>

      <div class="settings-grid" >
        <div class="settings-card">
          <div class="card-title">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/></svg>
            تغيير لون الخلفية
          </div>
          
          <label class="field-label">اختر اللون</label>
          <div class="color-picker-grid">
            <div class="color-circle transparent-bg" :class="{active: backgroundColor === 'transparent'}" @click="changeBG('transparent')" title="شفاف"></div>
            <div class="color-circle" style="background: #ffffff;" :class="{active: backgroundColor === '#ffffff'}" @click="changeBG('#ffffff')" title="أبيض"></div>
            <div class="color-circle" style="background: #f3f4f6;" :class="{active: backgroundColor === '#f3f4f6'}" @click="changeBG('#f3f4f6')" title="رمادي"></div>
            <div class="color-circle" style="background: #6366f1;" :class="{active: backgroundColor === '#6366f1'}" @click="changeBG('#6366f1')" title="بنفسجي"></div>
            <div class="custom-color-input" title="لون مخصص">
              <input type="color" v-model="customColor" @input="changeBG(customColor)">
            </div>
          </div>
        </div>

        <div class="settings-card">
          <div class="card-title">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            إعدادات التصدير
          </div>

          <label class="field-label">صيغة الصورة</label>
          <div class="select-wrap">
            <select v-model="outputFormat" @change="applySettings">
              <option value="image/png">PNG (أفضل جودة وشفافية)</option>
              <option value="image/jpeg">JPG (أصغر حجم، بدون شفافية)</option>
              <option value="image/webp">WebP (الأفضل للمتاجر والمواقع)</option>
            </select>
            <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </div>

      <div class="action-area" v-if="processedImage">
        <button class="download-btn" @click="downloadImage">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          تحميل الصورة النهائية
        </button>
      </div>
    </div>

    <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileChange" />
  </div>
</template>

<script>
import { notifyToolSuccess } from '@/utils/promoPopup';

export default {
  name: 'BackgroundRemoval',
  data() {
    return {
      currentLanguage: this.$store.state.currentLanguage,
      isDragging: false,
      originalImage: null,
      originalFile: null,
      originalDims: '',
      
      // Processing State
      isProcessing: false,
      progress: 0,
      processingStage: 'idle',
      elapsedSeconds: 0,
      processingTimer: null,
      rawTransparentBlob: null,
      processedImage: null,
      
      // Settings
      backgroundColor: 'transparent',
      customColor: '#ff0000',
      outputFormat: 'image/png'
    };
  },
  computed: {
    visualProgress() { if (this.processingStage === 'download') return Math.min(this.progress, 85); if (this.processingStage === 'segment') return Math.min(96, 86 + Math.floor(this.elapsedSeconds / 8)); if (this.processingStage === 'finalize') return 98; return this.progress },
    processingMessage() { if (this.currentLanguage === 'en') return this.processingStage === 'download' ? `Loading AI model… ${this.progress}%` : this.processingStage === 'finalize' ? 'Preparing your result…' : 'AI is separating the subject from the background…'; return this.processingStage === 'download' ? `جارٍ تحميل نموذج الذكاء الاصطناعي… ${this.progress}%` : this.processingStage === 'finalize' ? 'جارٍ تجهيز النتيجة…' : 'يفصل الذكاء الاصطناعي العنصر عن الخلفية الآن…' },
    elapsedLabel() { return this.currentLanguage === 'en' ? `Working for ${this.elapsedSeconds}s` : `مدة المعالجة: ${this.elapsedSeconds} ثانية` }
  },
  beforeDestroy() { this.stopProcessingTimer(); },
  methods: {
    startProcessingTimer() { this.stopProcessingTimer(); this.elapsedSeconds = 0; this.processingTimer = window.setInterval(() => { this.elapsedSeconds += 1 }, 1000) },
    stopProcessingTimer() { if (this.processingTimer) window.clearInterval(this.processingTimer); this.processingTimer = null },
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) this.loadFile(file);
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) this.loadFile(file);
    },
    loadFile(file) {
      this.originalFile = file;
      this.resetSettings();
      
      const reader = new FileReader();
      reader.onload = (e) => {
        this.originalImage = e.target.result;
        const img = new Image();
        img.onload = () => {
          this.originalDims = `${img.naturalWidth} × ${img.naturalHeight}`;
          this.processBackground();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },
async processBackground() {
      if (!this.originalFile) return;
      
      this.isProcessing = true;
      this.progress = 0;
      this.processingStage = 'download';
      this.startProcessingTimer();
      this.rawTransparentBlob = null;
      this.processedImage = null;

      try {
        const { default: imglyRemoveBackground } = await import("@imgly/background-removal");
        
        this.rawTransparentBlob = await imglyRemoveBackground(this.originalFile, {
          progress: (key, current, total) => {
            // Only update percentage when it's actively downloading (fetching) the models
            if (key.includes('fetch') && total > 0) {
              this.processingStage = 'download';
              const percent = Math.round((current / total) * 100);
              if (percent > this.progress) {
                this.progress = percent;
              }
            }
            if (!key.includes('fetch')) this.processingStage = 'segment';
          }
        });
        
        // At this point, the background is fully removed.
        this.progress = 100; 
        this.processingStage = 'finalize';
        await this.applySettings();

      } catch (error) {
        console.error("Failed to remove background:", error);
        alert(this.currentLanguage === 'en' ? "Error removing background." : "حدث خطأ أثناء إزالة الخلفية.");
      } finally {
        this.isProcessing = false;
        this.processingStage = 'idle';
        this.stopProcessingTimer();
      }
    },
    changeBG(color) {
      this.backgroundColor = color;
      this.applySettings();
    },
    async applySettings() {
      if (!this.rawTransparentBlob) return;

      const img = new Image();
      const url = URL.createObjectURL(this.rawTransparentBlob);
      
      await new Promise((resolve) => {
        img.onload = resolve;
        img.src = url;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');

      if (this.backgroundColor !== 'transparent') {
        ctx.fillStyle = this.backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else if (this.outputFormat === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(img, 0, 0);
      this.processedImage = canvas.toDataURL(this.outputFormat, 1.0);
      notifyToolSuccess('background-remover');
      URL.revokeObjectURL(url);
    },
    downloadImage() {
      if (!this.processedImage) return;
      
      const ext = this.outputFormat.split('/')[1];
      const name = (this.originalFile?.name || 'image').replace(/\.[^.]+$/, '') + `-nobg.${ext}`;
      
      const link = document.createElement("a");
      link.href = this.processedImage;
      link.download = name;
      link.click();
    },
    resetSettings() {
      this.backgroundColor = 'transparent';
      this.outputFormat = 'image/png';
    },
    reset() {
      this.stopProcessingTimer();
      this.originalImage = null;
      this.originalFile = null;
      this.processedImage = null;
      this.rawTransparentBlob = null;
      this.$refs.fileInput.value = '';
      this.resetSettings();
    }
  },
    watch: {
    '$store.state.currentLanguage': function(newLanguage) {
      this.currentLanguage = newLanguage;
    }
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.compressor-wrapper {
  font-family: 'DM Sans', 'Cairo', sans-serif;
  max-width: 960px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
  background: #F6F9FB;
  min-height: 100vh;
}
[dir="rtl"] .compressor-wrapper {
  direction: rtl;
  text-align: right;
}

/* ── Drop Zone ── */
.drop-zone {
  background: white;
  border: 2.5px dashed #3a7afe;
  border-radius: 20px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color .2s, background .2s;
  margin-bottom: 1.5rem;
}
.drop-zone.dragging { border-color: #3a7afe; background: #eef0ff; }
.drop-zone.has-file { cursor: default; padding: 1.5rem; }

.drop-icon svg {
  width: 52px; height: 52px;
  fill: #3a7afe; margin-bottom: 1rem; opacity: .85;
}
.drop-text { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 0 0 .35rem; }
.drop-hint { color: #9ca3af; font-size: .85rem; margin: 0 0 .8rem; }
.drop-formats { color: #9ca3af; font-size: .8rem; margin: .8rem 0 0; }

.choose-btn {
  background: #3a7afe; color: white; border: none; border-radius: 10px;
  padding: .6rem 1.6rem; font-family: inherit; font-size: .95rem;
  font-weight: 600; cursor: pointer; transition: background .2s, transform .1s;
}
.choose-btn:hover { background: #2e32d4; transform: translateY(-1px); }

/* ── Preview Area ── */
.preview-area {
  display: flex; gap: 1rem; align-items: center; justify-content: center; flex-wrap: wrap;
}
.preview-card {
  flex: 1; min-width: 180px; max-width: 320px; background: #f9fafb;
  border-radius: 14px; overflow: hidden; border: 1.5px solid #e5e7eb;
}
.preview-label {
  font-size: .75rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: #6b7280; padding: .5rem .8rem; border-bottom: 1px solid #e5e7eb; background: white;
}
.preview-img {
  width: 100%; height: 200px; object-fit: contain; display: block;
}

/* Checkered background class */
.transparent-bg {
  background-color: #eee;
  background-image: linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%, #ddd),
                    linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%, #ddd);
  background-size: 20px 20px;
  background-position: 0 0, 10px 10px;
}

.preview-placeholder {
  width: 100%; height: 200px; display: flex; align-items: center; justify-content: center;
  color: #9ca3af; font-size: .85rem; background: #f9fafb;
}
.preview-meta {
  display: flex; gap: .5rem; padding: .5rem .8rem; align-items: center; justify-content: center;
  background: white; border-top: 1px solid #e5e7eb; flex-wrap: wrap;
}
.dim-badge {
  font-family: 'DM Mono', monospace; font-size: .75rem; border-radius: 6px; padding: .2rem .5rem;
  background: #f3f4f6; color: #4b5563;
}

.preview-arrow {
  display: flex; flex-direction: column; align-items: center; gap: .4rem; color: #3a7afe;
}
.preview-arrow svg { width: 28px; height: 28px; }

.spinner {
  width: 30px; height: 30px; border: 4px solid #e5e7eb;
  border-top-color: #3a7afe; border-radius: 50%;
  animation: spin .7s linear infinite;
}
.processing-status{display:grid;width:190px;gap:5px;margin-top:8px;text-align:center;color:#24365c;font-size:.76rem}.processing-status strong{font-size:.8rem}.processing-status>span,.processing-status small{color:#6b7280;font-weight:500}.progress-track{height:7px;overflow:hidden;background:#e5e7eb;border-radius:999px}.progress-track span{display:block;height:100%;background:#3a7afe;border-radius:inherit;transition:width .35s ease}.processing-status small{line-height:1.35}
@keyframes spin { to { transform: rotate(360deg); } }

.reset-btn {
  display: flex; align-items: center; gap: .4rem; margin: 1.2rem auto 0;
  background: none; border: 1.5px solid #d1d5db; border-radius: 8px;
  padding: .4rem 1rem; font-family: inherit; font-size: .85rem;
  color: #6b7280; cursor: pointer; transition: border-color .2s, color .2s;
}
.reset-btn svg { width: 14px; height: 14px; }
.reset-btn:hover { border-color: #3a7afe; color: #3a7afe; }

/* ── Settings Grid & Color Picker ── */
.settings-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 1.2rem; margin-bottom: 1.5rem;
}
@media (max-width: 600px) { .settings-grid { grid-template-columns: 1fr; } }

.settings-card {
  background: white; border-radius: 16px; padding: 1.4rem; border: 1.5px solid #e5e7eb;
}
.card-title {
  display: flex; align-items: center; gap: .5rem; font-weight: 700; font-size: 1rem;
  color: #111827; margin-bottom: 1.1rem;
}
.card-title svg { width: 18px; height: 18px; color: #3a7afe; }

.field-label {
  display: block; font-size: .8rem; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: .8rem;
}

/* Color Palette */
.color-picker-grid {
  display: flex; gap: 12px; flex-wrap: wrap; align-items: center;
}
.color-circle {
  width: 42px; height: 42px; border-radius: 10px; cursor: pointer;
  border: 2px solid #e2e8f0; transition: transform 0.2s, border-color 0.2s;
}
.color-circle:hover { transform: scale(1.1); }
.color-circle.active { border: 2px solid #3a7afe; box-shadow: 0 0 0 2px rgba(58, 122, 254, 0.2); }

.custom-color-input {
  position: relative; width: 42px; height: 42px; border-radius: 10px;
  overflow: hidden; border: 2px solid #e2e8f0;
  background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
}
.custom-color-input input[type="color"] {
  position: absolute; top: -5px; left: -5px; width: 60px; height: 60px;
  cursor: pointer; opacity: 0;
}

/* Select */
.select-wrap { position: relative; }
.select-wrap select {
  width: 100%; appearance: none; background: #f9fafb; border: 1.5px solid #e5e7eb;
  border-radius: 10px; padding: .6rem 2.2rem .6rem .9rem; font-family: inherit;
  font-size: .9rem; color: #374151; cursor: pointer; outline: none; transition: border-color .2s;
}
.select-wrap select:focus { border-color: #3a7afe; }
.select-arrow {
  position: absolute; right: .7rem; top: 50%; transform: translateY(-50%);
  width: 16px; height: 16px; color: #6b7280; pointer-events: none;
}
[dir="rtl"] .select-arrow { right: auto; left: .7rem; }
[dir="rtl"] .select-wrap select { padding: .6rem .9rem .6rem 2.2rem; }

/* ── Action Area ── */
.action-area { text-align: center; }
.download-btn {
  display: inline-flex; align-items: center; gap: .6rem; background: #3a7afe;
  color: white; border: none; border-radius: 12px; padding: .85rem 2.2rem;
  font-family: inherit; font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: background .2s, transform .1s; box-shadow: 0 4px 16px rgba(59,63,232,.3);
}
.download-btn svg { width: 18px; height: 18px; }
.download-btn:hover { background: #2e32d4; transform: translateY(-2px); }
</style>
